import type { TFunction } from "i18next";
import type React from "react";
import { toast } from "@/hooks/useToast";

interface UserInfo {
  id: number;
  name: string;
  avatarUrl: string | null;
}

interface BlockUserConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  dialogTitle: string;
  dialogDescription: string;
  isBlocked: boolean;
  isBlocking: boolean;
  isUnblocking: boolean;
  user: UserInfo;
  blockUser: (id: number) => Promise<boolean>;
  unblockUser: (id: number) => Promise<boolean>;
  t: TFunction;
  isHandlingActionRef: React.MutableRefObject<boolean>;
}

export const BlockUserConfirmDialog: React.FC<BlockUserConfirmDialogProps> = ({
  isOpen,
  onClose,
  dialogTitle,
  dialogDescription,
  isBlocked,
  isBlocking,
  isUnblocking,
  user,
  blockUser,
  unblockUser,
  t,
  isHandlingActionRef,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10002]" onClick={(e) => e.stopPropagation()}>
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="-translate-x-1/2 -translate-y-1/2 fixed top-1/2 left-1/2 z-50 w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
        <h2 className="font-semibold text-lg">{dialogTitle}</h2>
        <p className="mt-2 text-gray-500 text-sm">{dialogDescription}</p>

        <div className="mt-4 flex justify-end space-x-2">
          <button
            type="button"
            className="rounded-md border border-gray-300 px-4 py-2 font-medium text-sm"
            onClick={onClose}
            disabled={isBlocking || isUnblocking}
          >
            {t("common.cancel")}
          </button>

          <button
            type="button"
            className={`rounded-md px-4 py-2 font-medium text-sm text-white ${
              (isBlocked && !isBlocking) || isUnblocking
                ? "bg-blue-600 hover:bg-blue-700"
                : "bg-red-600 hover:bg-red-700"
            }`}
            onClick={async () => {
              console.log("Custom dialog confirm button clicked");
              if (isHandlingActionRef.current) return;
              isHandlingActionRef.current = true;

              try {
                if (isBlocked) {
                  console.log("Attempting to unblock user:", user.id);
                  await unblockUser(user.id);
                  toast({
                    title: t("common.userUnblockSuccess", "User unblocked"),
                    description: t(
                      "common.userUnblockSuccessMessage",
                      "You have unblocked this user.",
                    ),
                    variant: "success",
                  });
                } else {
                  console.log("Attempting to block user:", user.id);
                  await blockUser(user.id);
                  toast({
                    title: t("common.userBlockSuccess", "User blocked"),
                    description: t("common.userBlockSuccessMessage", "You have blocked this user."),
                    variant: "success",
                  });
                }
              } catch (error) {
                console.error("Error blocking/unblocking user:", error);
                toast({
                  title: t("common.error", "Error"),
                  description:
                    isBlocked || isUnblocking
                      ? t("common.unblockUserError", "Failed to unblock user. Please try again.")
                      : t("common.blockUserError", "Failed to block user. Please try again."),
                  variant: "error",
                });
              } finally {
                isHandlingActionRef.current = false;
                onClose();
              }
            }}
            disabled={isBlocking || isUnblocking}
          >
            {isBlocking || isUnblocking ? (
              <>
                <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {isBlocking ? t("common.blocking") : t("common.unblocking")}
              </>
            ) : isBlocked ? (
              t("common.unblock")
            ) : (
              t("common.block")
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
